import { ConfiguratorId, ConfiguratorOption, LinkedOption, ListOption, ListOptionCategory, ListOptionItem, NumberOption, OperatorNote, OperatorNoteInput, OptionCode, OptionId, OptionVisibility, RALColorOption, TextInfoOption, TextOption } from "#models/Configurator"
import * as Effect from "effect-app/Effect"
import * as Struct from "effect/Struct"
import { S, TaggedRequestFor } from "../../lib.ts"
import { ConfiguratorCategoryView, ConfiguratorOptionGroupEntryView, ConfiguratorPriceTableEntryView } from "./Views.ts"

// codegen:start {preset: meta, sourcePrefix: src/resources/ }
const Req = TaggedRequestFor("Admin/Configurator/Option")
// codegen:end

export class ConfiguratorOptionListView
  extends S.Opaque<ConfiguratorOptionListView, ConfiguratorOptionListView.Encoded>()(
    S
      .Struct({
        // ...Utils.pick(fields, "id", "_tag", "code", "isEnabled", "visibility", "name")
        _tag: S.Literals(["number", "text", "list", "text_info", "ral_color"]),
        id: OptionId.withConstructorDefault,
        code: OptionCode,
        name: S.NonEmptyString255,
        isEnabled: S.Boolean.withConstructorDefault,
        visibility: OptionVisibility.pipe(S.withConstructorDefault(Effect.succeed("visible" as const))),
        note: OperatorNote,
        linkedOptions: S.NullOr(S.Array(LinkedOption.fields.optionId))
      })
      .annotate({ identifier: "ConfiguratorOptionListView" })
  )
{
}

export class ConfiguratorOptionGroupView
  extends S.Opaque<ConfiguratorOptionGroupView, ConfiguratorOptionGroupView.Encoded>()(
    S
      .Struct({
        id: S.StringId.withConstructorDefault,
        name: S.NonEmptyString255,
        isOptional: S.Boolean,
        options: S.Array(ConfiguratorOptionListView).withConstructorDefault
      })
      .annotate({ identifier: "ConfiguratorOptionGroupView" })
  )
{}

export class AllConfiguratorOptionsResponse extends S.Opaque<AllConfiguratorOptionsResponse>()(
  S
    .Struct({
      optionGroups: S.Array(ConfiguratorOptionGroupView),
      priceTables: S.Array(ConfiguratorPriceTableEntryView),
      category: S.NullOr(ConfiguratorCategoryView)
    })
    .annotate({ identifier: "AllConfiguratorOptionsResponse" })
) {}

export class AllConfiguratorOptions extends Req.Query<AllConfiguratorOptions>()("AllConfiguratorOptions", {
  id: ConfiguratorId
}, { success: AllConfiguratorOptionsResponse }) {}

// codegen:start {preset: model}
//
export namespace ConfiguratorOptionListView {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorOptionListView> {}
}
export namespace ConfiguratorOptionGroupView {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorOptionGroupView> {}
}
//
// codegen:end

export class ListOptionCategoryInput extends S.Opaque<ListOptionCategoryInput>()(
  S
    .Struct({
      ...Struct.omit(ListOptionCategory.fields, ["timestamps"])
    })
    .annotate({ identifier: "ListOptionCategoryInput" })
) {}

export class ListOptionItemInput extends S.Opaque<ListOptionItemInput>()(
  S
    .Struct({
      ...Struct.omit(ListOptionItem.fields, ["timestamps"])
    })
    .annotate({ identifier: "ListOptionItemInput" })
) {}

export class NumberOptionInput extends S.Opaque<NumberOptionInput>()(
  S
    .Struct({
      ...Struct.omit(NumberOption.fields, ["timestamps", "id"]),
      note: OperatorNoteInput
    })
    .annotate({ identifier: "NumberOptionInput" })
) {}

export class TextOptionInput extends S.Opaque<TextOptionInput>()(
  S
    .Struct({
      ...Struct.omit(TextOption.fields, ["timestamps", "id"]),
      note: OperatorNoteInput
    })
    .annotate({ identifier: "TextOptionInput" })
) {}

export class RALColorOptionInput extends S.Opaque<RALColorOptionInput>()(
  S
    .Struct({
      ...Struct.omit(RALColorOption.fields, ["timestamps", "id"]),
      note: OperatorNoteInput
    })
    .annotate({ identifier: "RALColorOptionInput" })
) {}

export class ListOptionInput extends S.Opaque<ListOptionInput>()(
  S
    .Struct({
      ...Struct.omit(ListOption.fields, ["timestamps", "id", "items"]),
      note: OperatorNoteInput,
      categories: S.Array(ListOptionCategoryInput),
      items: S.NonEmptyArray(ListOptionItemInput)
    })
    .annotate({ identifier: "ListOptionInput" })
) {}

export class TextInfoOptionInput extends S.Opaque<TextInfoOptionInput>()(
  S
    .Struct({
      ...Struct.omit(TextInfoOption.fields, ["timestamps", "id"]),
      note: OperatorNoteInput
    })
    .annotate({ identifier: "TextInfoOptionInput" })
) {}

export const ConfiguratorOptionInput = S.Union([
  ListOptionInput,
  NumberOptionInput,
  TextOptionInput,
  RALColorOptionInput,
  TextInfoOptionInput
])
export type ConfiguratorOptionInput = typeof ConfiguratorOptionInput.Type

export class CreateConfiguratorOptionResponse extends S.Opaque<CreateConfiguratorOptionResponse>()(
  S
    .Struct({
      id: S.StringId
    })
    .annotate({ identifier: "CreateConfiguratorOptionResponse" })
) {}

export class CreateConfiguratorOption extends Req.Command<CreateConfiguratorOption>()("CreateConfiguratorOption", {
  configuratorId: ConfiguratorId,
  option: ConfiguratorOptionInput,
  optionGroupId: S.StringId
}, { success: CreateConfiguratorOptionResponse }) {}

export class GetConfiguratorOptionResponse extends S.Opaque<GetConfiguratorOptionResponse>()(
  S
    .Struct({
      option: ConfiguratorOption,
      optionGroupId: S.StringId,
      configuratorCategory: S.NullOr(ConfiguratorCategoryView),
      priceTables: S.Array(ConfiguratorPriceTableEntryView),
      optionGroups: S.Array(ConfiguratorOptionGroupEntryView)
    })
    .annotate({ identifier: "GetConfiguratorOptionResponse" })
) {}
export class GetConfiguratorOption extends Req.Query<GetConfiguratorOption>()("GetConfiguratorOption", {
  id: S.StringId,
  configuratorId: ConfiguratorId
}, { success: GetConfiguratorOptionResponse }) {}

export class CloneConfiguratorOption extends Req.Command<CloneConfiguratorOption>()("CloneConfiguratorOption", {
  configuratorId: ConfiguratorId,
  id: S.StringId
}, { success: S.Struct({ id: S.StringId }) }) {}

export class RemoveConfiguratorOption extends Req.Command<RemoveConfiguratorOption>()("RemoveConfiguratorOption", {
  configuratorId: ConfiguratorId,
  id: S.StringId
}, {
  // what RestoreConfiguratorOption needs to undo it
  success: S.Struct({
    option: ConfiguratorOption,
    optionGroupId: S.StringId,
    index: S.NonNegativeInt
  })
}) {}

export class RestoreConfiguratorOption extends Req.Command<RestoreConfiguratorOption>()("RestoreConfiguratorOption", {
  configuratorId: ConfiguratorId,
  optionGroupId: S.StringId,
  option: ConfiguratorOption,
  index: S.NonNegativeInt
}) {}

// Writes only the note, so the list can save it without the rest of the option.
export class SetConfiguratorOptionNote extends Req.Command<SetConfiguratorOptionNote>()("SetConfiguratorOptionNote", {
  configuratorId: ConfiguratorId,
  id: S.StringId,
  note: OperatorNoteInput
}) {}

export class SortConfiguratorOption extends Req.Command<SortConfiguratorOption>()("SortConfiguratorOption", {
  configuratorId: ConfiguratorId,
  optionGroupId: S.StringId,
  order: S.Array(S.StringId)
}) {}

export class UpdateConfiguratorOption extends Req.Command<UpdateConfiguratorOption>()("UpdateConfiguratorOption", {
  configuratorId: ConfiguratorId,
  id: S.StringId,
  option: ConfiguratorOptionInput,
  optionGroupId: S.StringId
}) {}
